import {
  SkyButton,
  SkyCard,
  SkyMessageBar,
  SkySwitch,
  SkyText,
  SkyTextarea,
} from "@skypointcloud/skypoint-ui";
import { useState } from "react";
import {
  DOMAINS_REQUIRED_MESSAGE,
  NOTIFICATION_DOMAINS_MAX,
  isValidDomain,
  normaliseDomain,
  type NotificationDomainsView,
  type RecipientPolicy,
} from "@shared/types/notificationDomains";
import { LoadingRows } from "@/components/access/ScreenStates";
import { ErrorState } from "@/components/platform/ScreenState";
import { useTenantInstance } from "@/contexts/TenantInstanceContext";
import {
  useNotificationDomains,
  useSaveNotificationDomains,
} from "@/hooks/use-notification-domains";
import { usePermissions } from "@/hooks/use-permissions";
import { InlineErrorBar, useInlineError } from "@/lib/feedback/InlineErrorBar";
import { useNotice } from "@/lib/feedback/NoticeProvider";

/**
 * #1111 (owner decision 2026-10-15): the tenant's optional "Allowed notification domains", at the
 * top of Settings > Notifications. A Tenant admin (`tenant.instances.manage`) turns it on and
 * lists the domains; it is off by default and applies to every instance of the tenant. While it
 * is on, an address outside the list is refused when a rule is saved, and one saved before is not
 * sent to. Anyone else who opens the tab (an Instance admin) reads where it stands, from the
 * rules view's `recipientPolicy`, and is told who changes it.
 */

interface Draft {
  enabled: boolean;
  domainsText: string;
}

function draftOf(view: NotificationDomainsView): Draft {
  return { enabled: view.enabled, domainsText: view.domains.join("\n") };
}

function domainLines(text: string): string[] {
  return [
    ...new Set(
      text
        .split(/\r?\n/)
        .map(normaliseDomain)
        .filter((line) => line.length > 0),
    ),
  ];
}

/** The box's own message, or null when it is fine. */
function domainsProblem(draft: Draft): string | null {
  const lines = domainLines(draft.domainsText);
  const invalid = lines.filter((line) => !isValidDomain(line));
  if (invalid.length > 0) {
    return `Not a domain name: ${invalid.join(", ")}. Enter one domain per line, for example contoso.com.`;
  }
  if (lines.length > NOTIFICATION_DOMAINS_MAX) {
    return `At most ${NOTIFICATION_DOMAINS_MAX} domains. This box has ${lines.length}.`;
  }
  if (draft.enabled && lines.length === 0) return DOMAINS_REQUIRED_MESSAGE;
  return null;
}

function sameDraft(a: Draft, b: Draft): boolean {
  return (
    a.enabled === b.enabled &&
    domainLines(a.domainsText).join() === domainLines(b.domainsText).join()
  );
}

/** "Main (2), Finance (1)": where saved addresses are outside the list. */
function outsideSentence(view: NotificationDomainsView): string | null {
  if (!view.enabled || view.outside.length === 0) return null;
  const where = view.outside.map((entry) => `${entry.instanceName} (${entry.count})`).join(", ");
  return `Saved addresses outside the list: ${where}. They are not sent to. Remove them from their rules to clear this.`;
}

function EditableDomains({
  tenantId,
  tenantName,
  view,
}: {
  tenantId: string;
  tenantName: string;
  view: NotificationDomainsView;
}) {
  const save = useSaveNotificationDomains(tenantId);
  const notice = useNotice();
  const saveError = useInlineError();
  const [draft, setDraft] = useState<Draft>(() => draftOf(view));
  const [touched, setTouched] = useState(false);
  const changed = !sameDraft(draft, draftOf(view));
  const problem = domainsProblem(draft);
  const outside = outsideSentence(view);
  // Shown once the reader has changed something (turning the switch on with an empty box
  // included) or pressed Save, and Save stays off while it shows (review of #1337).
  const shownProblem = problem !== null && (touched || changed) ? problem : null;

  function submit() {
    setTouched(true);
    saveError.clear();
    if (problem) return;
    save.mutate(
      { enabled: draft.enabled, domains: domainLines(draft.domainsText) },
      {
        onSuccess: (saved) => {
          setDraft(draftOf(saved));
          setTouched(false);
          notice.success(
            saved.enabled
              ? `Saved. Notification addresses in ${tenantName} must be in ${saved.domains.length === 1 ? "this domain" : `these ${saved.domains.length} domains`}.`
              : `Saved. Notification addresses in ${tenantName} can be in any domain.`,
          );
        },
        onError: (error) => saveError.show(error, "The allowed domains were not saved."),
      },
    );
  }

  return (
    <>
      <SkySwitch
        label="Only allow addresses in these domains"
        checked={draft.enabled}
        testId="allowed-domains-enabled"
        onCheckedChange={(checked) => setDraft({ ...draft, enabled: checked })}
      />
      <SkyTextarea
        label="Domains"
        value={draft.domainsText}
        rows={Math.min(Math.max(domainLines(draft.domainsText).length + 1, 2), 6)}
        hint={`One domain per line, for example contoso.com. Each domain is matched exactly, so list a subdomain on its own line. Up to ${NOTIFICATION_DOMAINS_MAX}.`}
        validationState={shownProblem ? "error" : "none"}
        validationMessage={shownProblem ?? undefined}
        testId="allowed-domains-list"
        onChange={(event) => setDraft({ ...draft, domainsText: event.target.value })}
      />
      {outside ? (
        <SkyMessageBar
          intent="warning"
          layout="multiline"
          testId="allowed-domains-outside"
          message={outside}
        />
      ) : null}
      <InlineErrorBar
        error={saveError.error}
        title="The allowed domains were not saved"
        onDismiss={saveError.clear}
        testId="allowed-domains-error"
      />
      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-3">
        <SkyText as="p" size={200} color="muted">
          {`Applies to every instance of ${tenantName}.`}
        </SkyText>
        <span className="ml-auto flex items-center gap-2">
          <SkyButton
            appearance="subtle"
            testId="allowed-domains-discard"
            disabled={!changed || save.isPending}
            onClick={() => {
              setDraft(draftOf(view));
              setTouched(false);
            }}
          >
            Discard
          </SkyButton>
          <SkyButton
            appearance="primary"
            testId="allowed-domains-save"
            disabled={!changed || problem !== null}
            loading={save.isPending}
            onClick={submit}
          >
            Save domains
          </SkyButton>
        </span>
      </div>
    </>
  );
}

/** What an Instance admin reads: where the setting stands, and who changes it. */
function ReadOnlyDomains({ policy }: { policy: RecipientPolicy }) {
  const { allowedDomains } = policy;
  return (
    <SkyText as="p" size={300} block testId="allowed-domains-summary">
      {allowedDomains.enabled
        ? `On. Notification addresses must be in ${allowedDomains.domains.join(", ")}. A Tenant admin changes this list.`
        : "Off. Notification addresses can be in any domain. A Tenant admin can limit them to your organization's domains."}
    </SkyText>
  );
}

function TenantAdminDomains({ policy }: { policy: RecipientPolicy }) {
  const { selectedInstance } = useTenantInstance();
  const tenantId = selectedInstance?.tenantId ?? null;
  const view = useNotificationDomains(tenantId);
  if (view.isError) {
    return (
      <>
        <ReadOnlyDomains policy={policy} />
        <ErrorState
          title="The allowed domains did not load"
          error={view.error}
          onRetry={() => void view.refetch()}
        />
      </>
    );
  }
  if (!view.data || !tenantId || !selectedInstance) return <LoadingRows rows={2} columns={1} />;
  return (
    <EditableDomains
      // Another tenant, or a save elsewhere, starts from what is stored.
      key={`${tenantId}-${view.data.updatedAt ?? "never"}`}
      tenantId={tenantId}
      tenantName={selectedInstance.tenantDisplayName}
      view={view.data}
    />
  );
}

export function AllowedDomainsCard({ policy }: { policy: RecipientPolicy }) {
  const { can, isResolved } = usePermissions();
  const manages = isResolved && can("tenant.instances.manage");
  return (
    <SkyCard className="flex flex-col gap-3 p-4" testId="allowed-domains">
      <div>
        <SkyText as="h2" size={400} weight="semibold" block>
          Allowed notification domains
        </SkyText>
        <SkyText as="p" size={300} color="muted" block className="mt-1">
          When this is on, rules can only send email to addresses in your organization&apos;s
          domains. A new address outside them is refused when a rule is saved, and one saved earlier
          is not sent to. Off by default.
        </SkyText>
      </div>
      {manages ? <TenantAdminDomains policy={policy} /> : <ReadOnlyDomains policy={policy} />}
    </SkyCard>
  );
}
